import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { rupiah, num, formatDate } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download } from "lucide-react";

const docDate = (r) => r.transaction_date || String(r.date || "").slice(0, 10);

function exportCsv(filename, headers, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
  const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Reports() {
  const [tab, setTab] = useState("stock");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [salesFilter, setSalesFilter] = useState("all");
  const [stock, setStock] = useState(null);
  const [inbound, setInbound] = useState([]);
  const [outbound, setOutbound] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [returns, setReturns] = useState([]);
  const [products, setProducts] = useState([]);
  const [stockFlow, setStockFlow] = useState([]);
  const [fleetFlow, setFleetFlow] = useState([]);
  const [salesList, setSalesList] = useState([]);
  const [scrap, setScrap] = useState({ rows: [], per_product: [], total_qty: 0, total_value: 0 });

  const load = useCallback(async () => {
    try {
      const qs = `date_from=${dateFrom}&date_to=${dateTo}`;
      const [a, b, c, d, e, f, g, h, i, j] = await Promise.all([
        api.get("/dashboard/stock-summary"),
        api.get("/inbound"), api.get("/outbound"), api.get("/invoices"), api.get("/returns"), api.get("/products"),
        api.get(`/reports/stock-flow?${qs}`), api.get(`/reports/fleet-flow?${qs}`),
        api.get("/sales-agents"),
        api.get(`/reports/scrap?${qs}&sales_id=${salesFilter === "all" ? "" : salesFilter}`),
      ]);
      setStock(a.data); setInbound(b.data); setOutbound(c.data); setInvoices(d.data); setReturns(e.data); setProducts(f.data);
      setStockFlow(g.data); setFleetFlow(h.data); setSalesList(i.data); setScrap(j.data);
    } catch (err) {
      console.error("Gagal memuat laporan:", err);
    }
  }, [dateFrom, dateTo, salesFilter]);
  useEffect(() => { load(); }, [load]);

  const inRange = (r) => {
    const t = docDate(r);
    if (dateFrom && t < dateFrom) return false;
    if (dateTo && t > dateTo) return false;
    return true;
  };
  const fInbound = inbound.filter(inRange);
  const fOutbound = outbound.filter(inRange);
  const fInvoices = invoices.filter(inRange);
  const fReturns = returns.filter(inRange);

  const exportCurrent = () => {
    const tag = `${dateFrom || "awal"}_${dateTo || "kini"}`;
    if (tab === "stock") exportCsv(`stok_${tag}.csv`, ["SKU", "Nama", "Stok Gudang", "Nilai Modal"], products.map(p => [p.sku, p.name, p.stock_warehouse, p.stock_warehouse * p.cost_price]));
    else if (tab === "inbound") exportCsv(`masuk_${tag}.csv`, ["No", "Tanggal", "Supplier", "Total"], fInbound.map(r => [r.trx_no, docDate(r), r.supplier_name, r.total]));
    else if (tab === "outbound") exportCsv(`keluar_${tag}.csv`, ["No", "Tanggal", "Sales", "Total"], fOutbound.map(r => [r.trx_no, docDate(r), r.sales_name, r.total]));
    else if (tab === "sales") exportCsv(`penjualan_${tag}.csv`, ["No", "Tanggal", "Sales", "Customer", "Total"], fInvoices.map(r => [r.trx_no, docDate(r), r.sales_name, r.customer, r.total]));
    else if (tab === "returns") exportCsv(`retur_${tag}.csv`, ["No", "Tanggal", "Sales", "Tujuan", "Nilai"], fReturns.map(r => [r.trx_no, docDate(r), r.sales_name, r.destination, r.total]));
    else if (tab === "stockflow") exportCsv(`alur_stok_${tag}.csv`, ["SKU", "Nama", "Saldo Awal", "Masuk", "Keluar", "Retur", "Adjustment", "Produksi", "Stok Akhir", "Nilai"], stockFlow.map(r => [r.sku, r.name, r.opening, r.in, r.out, r.ret, r.adj, r.prod_in + r.prod_out, r.ending, r.ending_value]));
    else if (tab === "fleetflow") exportCsv(`alur_armada_${tag}.csv`, ["Sales", "Kode", "SKU", "Nama", "Saldo Awal", "Diterima", "Terjual", "Kembali", "Retur Customer", "Adjustment", "Stok Akhir"], fleetFlow.flatMap(s => s.items.map(it => [s.name, s.code, it.sku, it.name, it.opening, it.received, it.sold, it.returned, it.cust_ret, it.adj, it.ending])));
    else if (tab === "scrap") exportCsv(`barang_rusak_${tag}.csv`, ["Tanggal", "No Ref", "Sumber", "Sales", "SKU", "Barang", "Kondisi", "Qty", "Nilai Modal"], scrap.rows.map(r => [r.date, r.ref_no, r.source, r.sales_name, r.sku, r.name, r.condition, r.qty, r.value]));
  };

  const destLabel = { sales: "Ke Armada", warehouse: "Ke Gudang", damaged: "Rusak" };

  return (
    <div data-testid="page-reports" className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Laporan</div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Laporan Lengkap</h1>
      </div>

      <Card className="p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label>Dari Tanggal</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="filter-date-from" />
        </div>
        <div>
          <Label>Sampai Tanggal</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="filter-date-to" />
        </div>
        {tab === "scrap" && (
          <div>
            <Label>Sales</Label>
            <Select value={salesFilter} onValueChange={setSalesFilter}>
              <SelectTrigger className="w-48" data-testid="filter-scrap-sales"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Sales</SelectItem>
                {salesList.map(s => <SelectItem key={s.sales_id} value={s.sales_id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {(dateFrom || dateTo) && <Button variant="ghost" size="sm" onClick={() => { setDateFrom(""); setDateTo(""); }} data-testid="btn-reset-filter">Reset</Button>}
        <Button variant="outline" className="ml-auto" onClick={exportCurrent} data-testid="btn-export-csv"><Download className="h-4 w-4 mr-1" />Export CSV</Button>
        <div className="w-full text-xs text-slate-400">Filter tanggal berlaku untuk semua tab kecuali Stok (snapshot saat ini). Tab Alur: movement sebelum "Dari Tanggal" digulung ke Saldo Awal.</div>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="stock" data-testid="tab-stock">Stok</TabsTrigger>
          <TabsTrigger value="inbound" data-testid="tab-inbound">Barang Masuk</TabsTrigger>
          <TabsTrigger value="outbound" data-testid="tab-outbound">Barang Keluar</TabsTrigger>
          <TabsTrigger value="sales" data-testid="tab-sales">Penjualan</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns">Retur</TabsTrigger>
          <TabsTrigger value="scrap" data-testid="tab-scrap">Barang Rusak</TabsTrigger>
          <TabsTrigger value="stockflow" data-testid="tab-stockflow">Alur Stok Gudang</TabsTrigger>
          <TabsTrigger value="fleetflow" data-testid="tab-fleetflow">Alur Armada</TabsTrigger>
        </TabsList>

        <TabsContent value="stock">
          <Card>
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">SKU</th><th>Nama</th><th className="text-right">Stok Gudang</th><th className="text-right">Nilai (Modal)</th></tr></thead>
              <tbody>
                {products.map(p => <tr key={p.product_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{p.sku}</td><td>{p.name}</td><td className="text-right font-mono">{num(p.stock_warehouse)}</td><td className="text-right font-mono">{rupiah(p.stock_warehouse * p.cost_price)}</td></tr>)}
              </tbody>
              <tfoot><tr className="font-bold bg-slate-50"><td colSpan={3} className="py-2 px-4 text-right">Total Nilai Gudang</td><td className="text-right font-mono">{rupiah(stock?.warehouse_stock_value || 0)}</td></tr></tfoot>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="inbound">
          <Card>
            <table className="w-full text-sm"><thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Supplier</th><th className="text-right">Total</th></tr></thead>
              <tbody>{fInbound.map(r => <tr key={r.inbound_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(docDate(r))}</td><td>{r.supplier_name}</td><td className="text-right font-mono">{rupiah(r.total)}</td></tr>)}</tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="outbound">
          <Card>
            <table className="w-full text-sm"><thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th className="text-right">Total</th></tr></thead>
              <tbody>{fOutbound.map(r => <tr key={r.outbound_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(docDate(r))}</td><td>{r.sales_name}</td><td className="text-right font-mono">{rupiah(r.total)}</td></tr>)}</tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="sales">
          <Card>
            <table className="w-full text-sm"><thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th>Customer</th><th className="text-right">Total</th></tr></thead>
              <tbody>{fInvoices.map(r => <tr key={r.invoice_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(docDate(r))}</td><td>{r.sales_name}</td><td>{r.customer}</td><td className="text-right font-mono">{rupiah(r.total)}</td></tr>)}</tbody>
            </table>
          </Card>
        </TabsContent>
        <TabsContent value="returns">
          <Card>
            <table className="w-full text-sm"><thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">No.</th><th>Tanggal</th><th>Sales</th><th>Tujuan</th><th className="text-right">Nilai</th></tr></thead>
              <tbody>{fReturns.map(r => <tr key={r.return_id} className="border-b"><td className="py-2 px-4 font-mono text-xs">{r.trx_no}</td><td>{formatDate(docDate(r))}</td><td>{r.sales_name}</td><td>{destLabel[r.destination] || r.destination}</td><td className="text-right font-mono">{rupiah(r.total)}</td></tr>)}</tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="scrap" className="space-y-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card className="p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Total Qty Rusak</div>
              <div className="font-mono font-bold text-xl text-slate-900 mt-1" data-testid="scrap-total-qty">{num(scrap.total_qty)}</div>
            </Card>
            <Card className="p-5">
              <div className="text-xs font-mono uppercase tracking-wider text-slate-500">Total Kerugian (Modal)</div>
              <div className="font-mono font-bold text-xl text-rose-700 mt-1" data-testid="scrap-total-value">{rupiah(scrap.total_value)}</div>
            </Card>
          </div>
          {scrap.per_product.length > 0 && (
            <Card>
              <div className="px-4 pt-3 text-xs font-mono uppercase tracking-wider text-slate-500">Ringkasan per Barang</div>
              <table className="w-full text-sm mt-1" data-testid="table-scrap-summary">
                <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left"><tr><th className="py-2 px-4">SKU</th><th>Nama</th><th className="text-right">Qty Rusak</th><th className="text-right">Nilai Kerugian</th></tr></thead>
                <tbody>
                  {scrap.per_product.map(p => <tr key={p.sku} className="border-b"><td className="py-2 px-4 font-mono text-xs">{p.sku}</td><td>{p.name}</td><td className="text-right font-mono">{num(p.qty)} {p.unit}</td><td className="text-right font-mono text-rose-700">{rupiah(p.value)}</td></tr>)}
                </tbody>
              </table>
            </Card>
          )}
          <Card>
            <table className="w-full text-sm" data-testid="table-scrap">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">Tanggal</th><th>No. Ref</th><th>Sumber</th><th>Sales</th><th>Barang</th><th>Kondisi</th><th className="text-right">Qty</th><th className="text-right">Nilai</th></tr>
              </thead>
              <tbody>
                {scrap.rows.map((r, i) => (
                  <tr key={r.ref_no + r.sku + i} className="border-b">
                    <td className="py-2 px-4">{formatDate(r.date)}</td>
                    <td className="font-mono text-xs">{r.ref_no}</td>
                    <td>{r.source}</td>
                    <td>{r.sales_name}</td>
                    <td><span className="font-mono text-xs">{r.sku}</span> · {r.name}</td>
                    <td><span className="text-rose-700">{r.condition}</span></td>
                    <td className="text-right font-mono">{num(r.qty)} {r.unit}</td>
                    <td className="text-right font-mono">{rupiah(r.value)}</td>
                  </tr>
                ))}
                {scrap.rows.length === 0 && <tr><td colSpan={8} className="py-8 text-center text-slate-400">Tidak ada barang rusak pada periode ini</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="stockflow">
          <Card>
            <div className="px-4 pt-3 text-xs text-slate-500">Dihitung dari ledger stock movement — Saldo Awal + Masuk − Keluar ± Adjustment + Produksi = Stok Akhir.</div>
            <table className="w-full text-sm" data-testid="table-stockflow">
              <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                <tr><th className="py-2 px-4">SKU</th><th>Nama</th><th className="text-right">Saldo Awal</th><th className="text-right">Masuk</th><th className="text-right">Keluar</th><th className="text-right">Retur</th><th className="text-right">Adjustment</th><th className="text-right">Produksi +/-</th><th className="text-right">Stok Akhir</th><th className="text-right">Nilai</th></tr>
              </thead>
              <tbody>
                {stockFlow.map(r => (
                  <tr key={r.product_id} className="border-b">
                    <td className="py-2 px-4 font-mono text-xs">{r.sku}</td><td>{r.name}</td>
                    <td className="text-right font-mono">{num(r.opening)}</td>
                    <td className="text-right font-mono">{num(r.in)}</td>
                    <td className="text-right font-mono text-rose-700">{num(r.out)}</td>
                    <td className="text-right font-mono">{num(r.ret)}</td>
                    <td className="text-right font-mono">{num(r.adj)}</td>
                    <td className="text-right font-mono">{num(r.prod_in + r.prod_out)}</td>
                    <td className="text-right font-mono font-bold">{num(r.ending)} {r.unit}</td>
                    <td className="text-right font-mono">{rupiah(r.ending_value)}</td>
                  </tr>
                ))}
                {stockFlow.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-slate-400">Belum ada data</td></tr>}
              </tbody>
            </table>
          </Card>
        </TabsContent>

        <TabsContent value="fleetflow" className="space-y-4">
          {fleetFlow.map(s => (
            <Card key={s.sales_id} data-testid={`fleetflow-${s.code}`}>
              <div className="px-4 pt-3 flex items-center"><div className="font-semibold">{s.name} <span className="text-xs text-slate-500 font-mono">{s.code}</span></div><div className="ml-auto font-mono text-sm">Nilai akhir: <span className="font-bold">{rupiah(s.ending_value)}</span></div></div>
              <table className="w-full text-sm mt-2">
                <thead className="bg-slate-50 text-xs font-mono uppercase tracking-wider text-slate-500 text-left">
                  <tr><th className="py-2 px-4">SKU</th><th>Nama</th><th className="text-right">Saldo Awal</th><th className="text-right">Diterima</th><th className="text-right">Terjual</th><th className="text-right">Kembali ke Gudang</th><th className="text-right">Retur Customer</th><th className="text-right">Adjustment</th><th className="text-right">Stok Akhir</th></tr>
                </thead>
                <tbody>
                  {s.items.map(it => (
                    <tr key={it.sku} className="border-b">
                      <td className="py-2 px-4 font-mono text-xs">{it.sku}</td><td>{it.name}</td>
                      <td className="text-right font-mono">{num(it.opening)}</td>
                      <td className="text-right font-mono">{num(it.received)}</td>
                      <td className="text-right font-mono text-rose-700">{num(it.sold)}</td>
                      <td className="text-right font-mono">{num(it.returned)}</td>
                      <td className="text-right font-mono">{num(it.cust_ret)}</td>
                      <td className="text-right font-mono">{num(it.adj)}</td>
                      <td className="text-right font-mono font-bold">{num(it.ending)} {it.unit}</td>
                    </tr>
                  ))}
                  {s.items.length === 0 && <tr><td colSpan={9} className="py-6 text-center text-slate-400">Tidak ada pergerakan</td></tr>}
                </tbody>
              </table>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
